export const THEME = {
  colors: {
    // DEPRECATED legacy blue — use `navy` (#0D1A42) for any brand/primary accent
    // (BRAND.md §4). These are unreferenced app-wide; kept only so nothing breaks
    // if a stray import remains. A commit hook blocks new THEME.colors.primary* use.
    primary:      "#1F4E79",
    primaryLight: "#2E6DA4",
    primaryDark:  "#163A5C",
    secondary:    "#D9D9D9",
    success:      "#C6EFCE",
    warning:      "#FFF2CC",
    danger:       "#F4CCCC",
    info:         "#DDEBF7",
    orange:       "#FCE4D6",
    white:        "#FFFFFF",
    lightGray:    "#F2F2F2",
    mediumGray:   "#888888",
    darkGray:     "#444444",
    text:         "#1A1A1A",
    textLight:    "#666666",
    border:       "#D0D0D0",

    // BRAND.md §4 — brand palette additions
    black:  "#000000",
    navy:   "#0D1A42", // brand accent — primary buttons, active state, brand moments
    ink:    "#231F20", // companion to navy; the second stop of the brand gradient
    silver: "#CDCDD2",
    // BRAND.md §4 — the brand atmosphere gradient used on hero surfaces (login,
    // marketing modules). Reuses the gradient embedded in the IA mark itself.
    brandGradient: "linear-gradient(to bottom, #0D1A42 0%, #231F20 100%)",

    // Categorical palette for multi-series charts (INVI HQ Insights). Brand
    // navy first, then brand-adjacent hues chosen for contrast on white.
    chartSeries: ["#0D1A42", "#5B6BB0", "#9AA6D4", "#C8A23C", "#6B6B6F"],

    // BRAND.md §4 — semantic colors (the brand semantics).
    // The legacy success/warning/danger keys above are Excel-mirror pastels — see BRAND.md §4 "Data-display exception".
    semantic: {
      danger:  "#DC2626",
      warning: "#D97706",
      success: "#059669",
      info:    "#2563EB", // saturated blue — informational / user-resolved state
    },

    // BRAND.md §4 — light/dark mode neutral palettes
    neutral: {
      light: {
        background:    "#F4F5F7",
        surface:       "#FFFFFF",
        border:        "#E4E5E9",
        textPrimary:   "#1A1A1F",
        textSecondary: "#6B6B6F",
      },
      dark: {
        background:    "#0A0A0B",
        surface:       "#131316",
        border:        "#1F1F22",
        textPrimary:   "#F4F4F6",
        textSecondary: "#B8B8BC",
      },
    },
  },
  fonts: {
    primary: "Inter, Arial, sans-serif",
    mono: "monospace",
  },
  // BRAND.md §5 — type scale. Each entry has size, weight, tracking, lineHeight.
  typography: {
    h1:      { size: "48px", weight: 600, tracking: "-1.5px", lineHeight: 1.2 },
    h2:      { size: "36px", weight: 600, tracking: "-1px",   lineHeight: 1.2 },
    h3:      { size: "24px", weight: 500, tracking: "-0.5px", lineHeight: 1.2 },
    h4:      { size: "18px", weight: 500, tracking: "0",      lineHeight: 1.2 },
    body:    { size: "16px", weight: 400, tracking: "0",      lineHeight: 1.5 },
    small:   { size: "14px", weight: 400, tracking: "0",      lineHeight: 1.4 },
    caption: { size: "12px", weight: 500, tracking: "0.5px",  lineHeight: 1.4 },
  },
  brand: {
    name: "INVI",
    fullName: "Inventory Network Variable Intelligence",
    company: "Dolan Auto Group",
  },
  spacing: {
    xs: "4px", sm: "8px", md: "16px",
    lg: "24px", xl: "32px", xxl: "48px",
    // BRAND.md §6 — additional scale values (4/8/12/16/24/32/48/64/96/128)
    s12:  "12px",
    s64:  "64px",
    s96:  "96px",
    s128: "128px",
  },
  borderRadius: {
    sm: "4px",
    md: "8px",
    lg: "12px",
    // BRAND.md §6 — additional radius values
    xl:   "16px",
    pill: "9999px",
  },
  sidebar: {
    width: 220,
    transitionDuration: "200ms",
    transitionEasing: "ease-out",
  },
  // Color-coded accent per vehicle-type bucket. Each accent reads well
  // both as a chip text color and as a chip background when run through
  // `tint(accent, 0.15)` — same compositing pattern as the stat-card
  // pattern (frontend-standards). Distinct hues so a row carrying multiple pills
  // ("Truck", "SUV", "Sedan / Coupe") is scannable at a glance.
  vehicleTypeColors: {
    truck:          "#2D5491", // slate blue
    suv:            "#285F3D", // forest green
    sedan_coupe:    "#4A3D8C", // indigo
    rv:             "#1E7D7D", // dark teal
    camper_trailer: "#8B5A1E", // dark amber
  },
  // Audit-remodel problem display (audit-remodel plan; design spec = the approved
  // mockup artifact). Severity colors reuse the brand semantics; the soft tints
  // are their chip/tile backgrounds. Vendor-status cells stay on the Excel-mirror
  // pastels (BRAND.md §4 data-display exception) so operators keep the workbook's
  // color muscle memory — always paired with a word, never color alone.
  problemDisplay: {
    severityColors: {
      critical: "#DC2626",   // semantic.danger
      warning:  "#D97706",   // semantic.warning
      info:     "#2563EB",   // semantic.info — the NEW pill and user-resolved state
      // Cleanup is hygiene with no sales impact (revenue-assurance plan): the
      // secondary-text grey, so it reads as low priority next to red and amber.
      cleanup:  "#6B6B6F",
      expected: "#059669",   // semantic.success
    },
    severitySoft: {
      critical: "#FBEAEA",
      warning:  "#FAF0E1",
      info:     "#E7EEFC",
      cleanup:  "#EEEEF1",
      expected: "#E3F2EC",
    },
    // The "Not on the Website" page's second serious problem — a sold car still
    // listed — wears its own strong colour so it reads as a different problem
    // from the cars shoppers cannot see (red), not a milder one (amber is
    // Warning). Ian, 2026-10-09, choosing indigo over teal and gold.
    soldListingAccent: "#4A3D8C",
    soldListingSoft:   "#ECE9F6",
    chipAccentBarWidth: 3,   // px — the severity bar inside a chip
    legendBackground: "#FAFAFB",  // mockup --surface-2: the legend strip's quiet ground
    bucketCells: {
      online:  { background: "#C6EFCE", label: "Online" },
      offline: { background: "#F4CCCC", label: "Offline" },
      missing: { background: "#FFF2CC", label: "No record" },
      pending: { background: "#D9D9D9", label: "Pending" },
      unknown: { background: "#FCE4D6", label: "Unrecognized" },
      // Phase 3 settled Tekion translation (mockup legend: Holding/Pipeline
      // share the in-process grey; Gone shares the inactive red; Blank warns).
      holding:  { background: "#D9D9D9", label: "Holding" },
      pipeline: { background: "#D9D9D9", label: "Pipeline" },
      gone:     { background: "#F4CCCC", label: "Gone" },
      blank:    { background: "#FCE4D6", label: "Blank" },
    },
    bucketCellText: "#1A1A1A",
    // Muted feed-name label printed above the bucket word in each matrix cell
    bucketCellFeedLabel: "#5A5F6A",
  },
  auditTable: {
    tabColors: {
      discrepancies:     "#F4CCCC",
      pending:           "#FFF2CC",
      completed:         "#C6EFCE",
      unclassified:      "#DDEBF7",
      dealer_duplicates: "#FCE4D6",
    },
    zebraLight:             "#FFFFFF",
    zebraMain:              "#F2F2F2",
    zebraDealerDup:         "#D6D6D6",
    headerBackground:       "#0D1A42", // brand navy — heavy data-table header
    titleBarBackground:     "#0D1A42", // brand navy — photo list sheet title bar
    titleBarText:           "#FFFFFF",
    titleBarBackgroundLight: "#EAECF0", // light gray — print-only title bar (saves toner; see index.css print rules)
    headerBackgroundLight:   "#FAFAFA", // light gray — print-only column header
    borderColor:            "#CDCDD2", // brand silver
    pinnedShadow:           "rgba(0, 0, 0, 0.18)",
    rowHoverBackground:     "#F8F8F8",
    rowSelectedBackground:  "#E8F0FE",
    activeTabBorderBottom:  "#0D1A42", // brand navy
    inactiveTabColor:       "#6B6B6F", // matches neutral.light.textSecondary
  },
  // Pop-up (modal) chrome: the dimmed backdrop behind the panel and the
  // panel's lift. Values match the existing modals (AuctionRowAlertModal,
  // ReprocessConfirmModal), which predate these tokens and still inline them.
  modal: {
    overlay: "rgba(0, 0, 0, 0.45)",
    shadow:  "0 8px 32px rgba(0, 0, 0, 0.15)",
  },
}
